import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import {
	FileDown,
	AudioLines,
	Home,
	AArrowDown,
	AArrowUp,
	Globe,
	Download,
	Share,
	Image,
	Bookmark,
	Smartphone,
	Info,
	FileText,
	BookOpen,
	LogOut,
} from 'lucide-react';

import config from '@/config';
import { Article } from '@/constants';

import styles from '../../issue/menu-button/MenuButton.module.css';

const MIN_FONT_SIZE = 30,
	MAX_FONT_SIZE = 100,
	FONT_STEP = 10;

export class ArticleButton {
	static ShareArticle = ShareArticleButton;
	static Library = Library;
	static Font = FontSizeButton;
	static Legal = Legal;
	static Links = Links;
}

function ShareArticleButton({
	article,
	language,
	onShare,
}: ShareArticleButtonProps) {
	const title: string = article.name[language] || undefined;
	const url: string = window.location.href;
	const message = 'URL copy to the clipboard';
	const onClick = () => {
		if (navigator.share) {
			navigator
				.share({
					title,
					url,
				})
				.then(() => {
					onShare(article);
					console.log('Successully share url:', {
						title,
						url,
					});
				})
				.catch(console.error);
			return;
		}

		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(url)
				.then(
					() => {
						onShare(article, 'clipboard');
						alert(message);
					},
					() => {
						alert('Fail copy the url');
					},
				)
				.catch(console.error);
			return;
		}

		const inputElement = document.createElement('input');
		document.body.appendChild(inputElement);
		inputElement.value = url;
		inputElement.select();
		document.execCommand('copy');
		document.body.removeChild(inputElement);
		onShare(article, 'clipboard');
		alert(message);
	};
	return (
		<button type={'button'} title={'Share'} onClick={onClick}>
			<span>Share</span>
			<span>
				<Share color={'#000'} />
			</span>
		</button>
	);
}

interface ShareArticleButtonProps {
	article: Article;
	language: string;
	onShare: (article: any, method?: 'os' | 'clipboard') => void;
}

function Library() {
	const navigate = useNavigate();
	return (
		<button
			type={'button'}
			onClick={() => {
				navigate({
					to: '/',
				});
			}}
		>
			<span>Library</span>
			<span>
				<Home color={'#000'} />
			</span>
		</button>
	);
}

function FontSizeButton({
	fontSize,
	setFontSize,
}: {
	fontSize: any;
	setFontSize: any;
}) {
	const animations = {
		hover: {
			scale: 1.1,
		},
		tap: {
			scale: 0.8,
		},
	};
	return (
		<div className={styles['font-button']}>
			<DecreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
			<span>Text Size</span>
			<IncreaseFontButton
				fontSize={fontSize}
				setFontSize={setFontSize}
				animations={animations}
			/>
		</div>
	);
}

function Links({ links }: LinkArgs) {
	if (!links || !links.length) {
		return null;
	}
	return (
		<section className={styles['links']}>
			{links
				.filter((l) => l.title && l.url)
				.map((l, i) => {
					const { url, title, icon } = l;
					return (
						<a key={i} href={url} target="_blank" rel="noreferrer">
							<h4>{title}</h4>
							<div></div>
						</a>
					);
				})}
		</section>
	);
}

interface LinkArgs {
	links: Array<LinkItem> | undefined;
}

export interface LinkItem {
	title: string;
	icon: string;
	url: string;
}

function DecreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const title = 'Decrease font size';

	const onClick = () => {
		const newFontSize = fontSize - FONT_STEP;
		setFontSize(newFontSize <= MIN_FONT_SIZE ? MIN_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MIN_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<AArrowDown color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<AArrowDown color={'#000'} />
		</motion.button>
	);
}

function Legal() {
	const components = [];
	const { title } = config.build;
	const { legal } = config.app;
	components.push(
		<div className="optanon-toggle-display">
			© Copyright {title}, Inc. All material that appears on this app is
			protected by United States copyright law and may not be reproduced,
			distributed, transmitted, cached, or otherwise used, except with
			prior written permission of {title}, Inc. 2024. All Rights Reserved.
		</div>,
	);
	if (legal?.tou) {
		components.push(
			<a href={legal.tou.url} target="_blank" rel="noreferrer">
				{legal.tou.label}
			</a>,
		);
	}
	if (legal?.privacy) {
		components.push(
			<a href={legal.privacy.url} target="_blank" rel="noreferrer">
				{legal.privacy.label}
			</a>,
		);
	}
	components.push(<div className="optanon-toggle-display">Do Not Sell</div>);
	if (legal?.cookie) {
		components.push(
			<a href={legal.cookie.url} target="_blank" rel="noreferrer">
				{legal.cookie.label}
			</a>,
		);
	}
	return (
		<div className={styles['legal']}>
			<div>{components}</div>
		</div>
	);
}

function IncreaseFontButton({ animations, fontSize, setFontSize }: FontButton) {
	const title = 'Increase font size';

	const onClick = () => {
		const newFontSize = fontSize + FONT_STEP;
		setFontSize(newFontSize >= MAX_FONT_SIZE ? MAX_FONT_SIZE : newFontSize);
	};

	const isDisabled = fontSize === MAX_FONT_SIZE;

	if (isDisabled) {
		return (
			<motion.button
				title={title}
				type={'button'}
				style={{ cursor: 'not-allowed' }}
				disabled
			>
				<AArrowUp color={'#c3c3c3'} />
			</motion.button>
		);
	}

	return (
		<motion.button
			title={title}
			whileHover={animations.hover}
			whileTap={animations.tap}
			type={'button'}
			style={{ cursor: 'pointer' }}
			onClick={onClick}
		>
			<AArrowUp color={'#000'} />
		</motion.button>
	);
}

interface FontButton {
	fontSize: any;
	setFontSize: any;
	animations: {
		hover: {
			scale: number;
		};
		tap: {
			scale: number;
		};
	};
}
